import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { BottomModal } from '../shared/BottomModal';
import { WALLET_UPDATED_EVENT } from '../../constants/events';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import config from '../../config';

const formatAmount = (amount) => Number(amount).toFixed(2).replace('.', ',');

// Карточка дня: «День X», монета + сумма (или подарок), кнопка «Забрать» на доступном дне.
// Забранный день — opacity 50, кнопка скрыта.
function RewardCard({ reward, isClaimed, isCurrent, isClaiming, onClaim }) {
  const isGift = reward.type === 'gift';

  return (
    <div
      className={`h-full relative rounded-[16px] border p-2  items-center gap-[3px] transition-all ${
        isCurrent && !isClaimed ? 'border-[#A1F69E] bg-white/10' : 'border-[#233636] bg-white/10'
      }`}
    >
      <div className={isClaimed ? 'opacity-10' : ''}>
        {isGift ? (
          reward.gift?.imageUrl ? (
            <div className='flex gap-5 justify-start items-center'>
              <img src={reward.gift.imageUrl} alt={reward.gift.title} className="size-[80px] object-contain" />
              <div>
                <p className="font-raleway font-bold text-[15px] text-white text-left">День {reward.day}</p>
                <p className="text-[#A1F69E] text-[16px] font-bold">Подарок "{reward.gift.title}"</p>
              </div>
            </div>
          ) : (
            <span className="text-[32px] leading-none">🎁</span>
          )
        ) : (
          <>
            <p className="font-raleway font-bold text-[15px] text-white text-center">День {reward.day}</p>
            <span className="flex items-center justify-center gap-[6px]">
              <img src="/icons/coin.svg" alt="монеты" className="w-[16px] h-[16px]" />
              <span className="font-raleway font-medium text-[20px] text-white">
                {formatAmount(reward.coinsAmount)}
              </span>
            </span>
            <p className="font-raleway font-bold text-[16px] text-[#A1F69E] text-center">x{reward.day}</p>
          </>
        )}
      </div>

      {isClaimed && (
        <img
          src="/icons/reward-check.svg"
          alt="reward-check"
          className="size-[40px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
        />
      )}
    </div>
  );
}

export function DailyRewardModal({ isOpen, onClose }) {
  const axiosPrivate = useAxiosPrivate();
  const [status, setStatus] = useState(null); // { rewards, streak, nextDay, claimedToday }
  const [isClaiming, setIsClaiming] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setStatus(null);
    axiosPrivate
      .get(config.API.DAILY_REWARD.STATUS)
      .then((res) => setStatus(res.data?.data))
      .catch((error) => {
        console.error('Ошибка загрузки ежедневной награды:', error);
        toast.error('Не удалось загрузить награду');
      });
  }, [isOpen]);

  const handleClaim = async () => {
    if (isClaiming || !status?.nextDay) return;

    setIsClaiming(true);
    try {
      const res = await axiosPrivate.post(config.API.DAILY_REWARD.CLAIM);
      const data = res.data?.data;

      setStatus((prev) => (prev ? { ...prev, claimedToday: true, streak: data.streak, nextDay: null } : prev));

      // Обновляем баланс/стрик в топбаре
      window.dispatchEvent(new Event(WALLET_UPDATED_EVENT));
    } catch (error) {
      const message = error?.response?.data?.message || 'Не удалось получить награду';
      toast.error(message);
    } finally {
      setIsClaiming(false);
    }
  };

  if (!isOpen) return null;

  // День, доступный к получению, либо последний полученный сегодня
  const currentDay = status?.claimedToday ? status.streak : status?.nextDay;
  const claimedDays = status?.claimedToday ? status.streak : Math.max(0, (status?.nextDay || 1) - 1);

  return createPortal(
    <BottomModal isOpen={isOpen} onClose={onClose}>
      <div className="px-4 pb-10 flex flex-col items-center text-white">
        {/* Шапка */}
        <p className="font-raleway font-semibold text-[22px] text-center">Ежедневный вход</p>
        <p className="font-raleway font-light text-[16px] text-center mt-[4px] mb-[18px] text-white/80">
          Заходите каждый день и получайте вознаграждения
        </p>

        {!status ? (
          <div className="flex justify-center py-10 w-full">
            <span className="font-raleway text-white/60">Загрузка...</span>
          </div>
        ) : (
          <>
            {/* Сетка наград: дни 1–6 в 2 колонки, 7-й (подарок) — на всю ширину */}
            <div className="w-full grid grid-cols-3 gap-[12px]">
              {status.rewards.map((reward) => (
                <div key={reward.day} className={reward.day === 7 ? 'col-span-3' : ''}>
                  <RewardCard
                    reward={reward}
                    isClaimed={reward.day <= claimedDays || (status.claimedToday && reward.day <= status.streak)}
                    isCurrent={reward.day === currentDay}
                    isClaiming={isClaiming}
                    onClaim={handleClaim}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Кнопка получения: скрывается, когда награда за сегодня уже получена */}
        {status && !status.claimedToday && !!status.nextDay && (
          <button
            onClick={handleClaim}
            disabled={isClaiming}
            className="mt-[16px] w-full p-[18.5px] rounded-[400px] bg-[#A1F69E] text-[#010D0D] font-raleway font-medium text-[18px] active:bg-[#7DD279] disabled:opacity-50 flex items-center justify-center gap-[10px]"
          >
            {isClaiming ? (
              <>
                <span className="w-[20px] h-[20px] rounded-full border-2 border-[#010D0D]/30 border-t-[#010D0D] animate-spin" />
                Получение...
              </>
            ) : (
              'Забрать приз'
            )}
          </button>
        )}
      </div>
    </BottomModal>,
    document.getElementById('root')
  );
}
